<template lang="pug">
#family-opinion-message-list
  // title
  .d-flex.flex-row.justify-content-center.mb-3
    .page-title 家族からの連絡

  // filter
  .mb-2.d-flex.flex-row.align-items-center.flex-wrap(v-if="!isBusy")
    span.table-option-text 利用者グループ:
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter"
        :options="filterOptions"
        size="md"
      )

  // message list
  FamilyReceiveMessageTable(
    v-model="messageItems"
    :fields="messageFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    :filter="filter"
    :filterFields="filterFields"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center(v-if="!isBusy")
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, mobileFamilyReceiveMessageStore, itemListFilterStore } from "@/store/store";
import { MobileFamilyReceiveMessageState } from "@/store/MobileFamilyReceiveMessage";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import { errorHandle } from "@/errorHandler";
import { Url } from "@/commons/constants/Url";
import Setting from "../../../../public/setting.json";

import OpinionsService, { OpinionGetModel } from "@/commons/services/Opinions";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import FamilyReceiveMessageTable, { FamilyReceiveMessageTableField, FamilyReceiveMessageTableItem } from "@/mobile/components/tables/FamilyReceiveMessageTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### メッセージ送信履歴(家族)コンポーネント
 */
@Component({
  components: {
    FamilyReceiveMessageTable
  }
})
export default class FamilyReceiveMessageList extends Vue {
  /** 意見サービス */
  @Inject("OpinionsService") opinionsService!: OpinionsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 読込み中か否か */
  isBusy: boolean = true;

  /** 受信メッセージ列名 */
  messageFields: Array<FamilyReceiveMessageTableField> = [];
  /** 受信メッセージの情報 */
  messageItems: Array<FamilyReceiveMessageTableItem> = [];
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.opinion.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.opinion.pageRange;

  /**
   * ### vue lifecycle created
   *
   * - 施設の表示権限で「受信メッセージ(opinion)」がfalseの場合は404画面へ遷移する
   */
  public created(): void {
    const opinionPermitted: boolean = userStore.facility.permittedFunctions["opinion"];
    if (opinionPermitted == false) {
      this.$router.push(Url.NotFound);
    }

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const messageFields: Array<FamilyReceiveMessageTableField> = this.createMessageFields();
    this.messageFields = messageFields;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.messageItems = [];
    this.currentRows = this.messageItems.length;
    await this.createView();

    // ページ番号の復元は画面初回表示時でのみ行う
    // pageRowが設定されるまえにcurrentPageを復元してもライブラリがページ遷移をしてくれないので情報取得後にページ番号を復元する
    this.currentPage = this.$route.params.page ? Number(this.$route.params.page) : 1;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createMessageFields(): Array<FamilyReceiveMessageTableField> {
    let fields: Array<FamilyReceiveMessageTableField> = [];
    fields.push({ key: "message", label: "", sortable: false, sortDirection: "desc", class: "" });

    return fields;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    // 匿名は利用者グループ=0として扱うので、選択項目を追加
    filterOptions.push({
      value: "0",
      text: "匿名"
    });

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + "-mobile"] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + "-mobile"];
    }
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const messages: Array<OpinionGetModel> = await this.getOpinions();
    const messageItems: Array<FamilyReceiveMessageTableItem> = this.createItems(messages);
    this.messageItems = this.messageItems.concat(messageItems);
    this.currentRows = this.messageItems.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(): Promise<void> {
    try {
      const count: number = await this.opinionsService.getCount(userStore);

      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 表示対象の意見を取得する
   */
  public async getOpinions(): Promise<Array<OpinionGetModel>> {
    // ページ番号の復元がありえるので、ページ番号を復元する
    // 復元後かつページ遷移した場合、復元したcurrentPageよりもthis.currentPageが大きくなるのでその場合はthis.currentPageを使用する
    let currentPage: number = this.$route.params.page ? Number(this.$route.params.page) : 1;
    currentPage = this.currentPage < currentPage ? currentPage : this.currentPage;

    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const startIndex: number = this.currentRows;
    const endIndex: number = this.perPage * (currentPage + this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };
    const response: Array<OpinionGetModel> = await this.opinionsService.gets(userStore, params);

    // 完了しているか否かを設定する
    // 完了日を見て判断する
    for (let opinion of response) {
      if (opinion.functionContents) {
        if (opinion.functionContents.finished) {
          opinion.functionContents.isFinished = true;
        } else {
          opinion.functionContents.isFinished = false;
        }
      }
    }

    return response;
  }

  /**
   * ### 受信メッセージの行を作成する
   */
  public createItems(opinions: Array<OpinionGetModel>): Array<FamilyReceiveMessageTableItem> {
    let items: Array<FamilyReceiveMessageTableItem> = [];

    for (const opinion of opinions) {
      items.push({
        functionContents: opinion.functionContents,
        residentId: opinion.residentId,
        residentName: opinion.residentName,
        familyId: opinion.familyId,
        familyName: opinion.familyName,
        datetime: Number(opinion.datetime),
        groupId: opinion.groupId,
        caseId: opinion.caseId
      });
    }

    return items;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: FamilyReceiveMessageTableItem): void {
    mobileFamilyReceiveMessageStore.updateMobileFamilyReceiveMessageState({ state: item as MobileFamilyReceiveMessageState });
    this.$router.push({ name: MobileUrl.FamilyReceiveMessageDetail, params: { type: "1", page: String(this.currentPage) } });
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + "-mobile", filter: this.filter });
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
#family-opinion-message-list {
  .filter {
    max-width: 160px;
  }
}
</style>
